import * as React from "react";
import { useHistory } from "react-router-dom";
import { models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { OutreachExportType } from "@app/network-outreach/export/enumerations";

interface NetworkOutreachDetailsProps {
  clientId: string;
  name: string;
  pageName: string;
  reportId: string;
  url: string;
  token: string;
  minimumQuarter: string;
  exportType: OutreachExportType;
  setCurrentQuarter: (quarter: string) => void;
  currentQuarter?: string;
  selectorId?: string;
}

const NetworkOutreachDetails: React.FC<React.PropsWithChildren<NetworkOutreachDetailsProps>> = (props: NetworkOutreachDetailsProps) => {
  const [error, setError] = React.useState("");
  const history = useHistory();

  const clientFilter = {
    $schema: "http://powerbi.com/product/schema#basic",
    filterType: models.FilterType.Basic,
    operator: "In",
    target: {
      column: "client_xid",
      table: "client_xids"
    },
    values: [props.clientId],
  }

  const minQuarterFilter = {
    $schema: "http://powerbi.com/product/schema#advanced",
    conditions: [
      {
        operator: "GreaterThanOrEqual",
        value: props.minimumQuarter
      }
    ],
    filterType: models.FilterType.Advanced,
    logicalOperator: "And",
    target: {
      column: "quarter",
      table: "quarters"
    }
  }

  const slicers: models.ISlicer[] = [];

  if (props.currentQuarter !== null) {
    const slicerFilter: models.ISlicer = {
      selector: new models.VisualSelector(props.selectorId),
      state: {
        filters: [
          {
            $schema: "http://powerbi.com/product/schema#basic",
            filterType: models.FilterType.Basic,
            operator: "In",
            target: {
              column: "Meta_Tag",
              table: "quarters"
            },
            values: [props.currentQuarter],
          }
        ]
      }
    };
    slicers.push(slicerFilter);
  }

  const config = {
    accessToken: props.token,
    embedUrl: props.url,
    filters: [clientFilter, minQuarterFilter],
    id: props.reportId,
    pageName: props.pageName,
    permissions: models.Permissions.Read,
    settings: {
      panes: {
        filters: {
          expanded: false,
          visible: false
        },
        pageNavigation: {
          visible: false
        }
      }
    },
    slicers: slicers,
    tokenType: models.TokenType.Embed,
    type: "report",
  };

  const createReport = () => {
    try {
      history.push({
        pathname: `${history.location.pathname}/export`,
        state: {
          currentQuarter: props.currentQuarter,
          exportType: props.exportType,
          minimumQuarter: props.minimumQuarter,
        }
      });
    } catch {
      setError("An error happened while creating the report.")
    }
  }

  return (
    <>
      <div className="row">
        {!!error && <QESAlert classNames="col-12" type="danger" persist={true}>{error}</QESAlert>}
      </div>
      <div className="row">
        <div className="col-6">
          <h5> {props.name} Outreach Metrics</h5>
          <p>Outreach displayed by {props.name}.</p>
        </div>
        <div className="col-6">
          <QESButton className="float-end" qesTestId="network-outreach__create-report" onClick={createReport} > Create Report </QESButton>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <PowerBIEmbed
            embedConfig={config}
            eventHandlers={
              new Map([
                //Additional Events that can be subscribed to can be found here - https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/handle-events
                ['dataSelected', (event: {
                  detail: {
                    dataPoints: models.IIdentityValue<{
                        equals: string,
                        target: models.IFilterColumnTarget 
                    }>[],
                  }
                }) => {
                  if (event.detail.dataPoints[0].identity[0].target.column === "Meta_Tag")
                    props.setCurrentQuarter(event.detail.dataPoints[0].identity[0].equals);
                }]
              ])
            }
            cssClassName={"powerbi-container"}
          />
        </div>
      </div>
    </>
  );
}

export { NetworkOutreachDetails, NetworkOutreachDetailsProps }